import React from 'react';
import { RefreshCw, AlertTriangle, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isDomainIssue: false 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    console.error('🔴 Error Boundary caught an error:', error, errorInfo);
    
    // Check if it's a domain/network issue
    const isDomainIssue = error.message.includes('Network Error') || 
                          error.message.includes('Failed to fetch') ||
                          error.message.includes('ERR_NAME_NOT_RESOLVED') ||
                          error.message.includes('ERR_CONNECTION_REFUSED');
    
    this.setState({ 
      error, 
      errorInfo, 
      isDomainIssue 
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Try to redirect to Railway URL if custom domain fails
    if (this.state.isDomainIssue && window.location.hostname === 'sabiwabee.com') {
      window.location.href = 'https://sabiwabeeai-production.up.railway.app';
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {this.state.isDomainIssue ? 'Connection Issue' : 'Something Went Wrong'}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {this.state.isDomainIssue 
                ? 'There seems to be a connection issue. This might be due to a temporary domain problem.'
                : 'An unexpected error occurred. Please try refreshing the page.'
              }
            </p>

            {this.state.isDomainIssue && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Try accessing the app directly at{' '}
                  <a 
                    href="https://sabiwabeeai-production.up.railway.app" 
                    className="underline hover:text-blue-600"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    sabiwabeeai-production.up.railway.app
                  </a>
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Try Again
              </button>
              
              {this.state.isDomainIssue && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-green-700 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <Home className="w-5 h-5" />
                  Go to Railway App
                </button>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-3 rounded overflow-auto">
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
